import { Test } from "./test.js";

export type CompareMethod = (a: Test, b: Test) => number;

/**
 * Represents a collection of tests with helper methods for filtering and sorting.
 * Extends the native Array for convenient test management.
 */
export class TestCollection extends Array<Test> {

  /**
   * Initializes a new TestCollection.
   * @param initialArray - Optional initial array of tests to populate the collection.
   */
  constructor (initialArray?: Test[]) {
    super();
    if (initialArray && initialArray.length > 0) {
      this.push(...initialArray);
    }
  }

  /**
   * Returns a new array containing the tests sorted by the specified comparison logic.
   * @param compareMethod - The function used to determine the sort order.
   * @returns A sorted array of tests.
   */
  sortBy(compareMethod: CompareMethod): Test[] {
    return this.slice().sort(compareMethod);
  }

  /**
   * Filters the collection for tests that exactly match the specified type bitmask.
   * @param isType - The type bitmask to match.
   * @returns An array of tests matching the type.
   */
  filterIs(isType: number): Test[] {
    return this.filter(x => x.type.is(isType));
  }

  /**
   * Filters the collection for tests that exactly match any of the specified type bitmasks.
   * @param isTypes - The type bitmasks to check against.
   * @returns An array of tests matching any of the types.
   */
  filterIsEither(...isTypes: number[]): Test[] {
    return this.filter(x => x.type.isEither(...isTypes));
  }

  /**
   * Filters the collection for tests that have at least one of the specified type bits set.
   * @param hasType - The type bit to check for.
   * @returns An array of tests containing the type bit.
   */
  filterHas(hasType: number): Test[] {
    return this.filter(x => x.type.has(hasType));
  }

  /**
   * Filters the collection for tests that have at least one of the specified type bits set from any of the arguments.
   * @param hasTypes - The type bits to check for.
   * @returns An array of tests containing any of the type bits.
   */
  filterHasEither(...hasTypes: number[]): Test[] {
    return this.filter(x => x.type.hasEither(...hasTypes));
  }

}

/**
 * SortByEnum
 */
export enum SortByEnum {
  /**
   * Sorts groups and tests
   */
  "all" = "all",
  /**
   * Sort groups only
   */
  "groupsOnly" = "groupsOnly",
  /**
   * Sort tests only
   */
  "testsOnly" = "testsOnly",
  /**
   * No sort
   */
  "none" = "none"
}

export const SortByMethod: Record<string, CompareMethod> = {
  /**
   * groupsOnly
   */
  groupsOnly: function (a, b) {
    // only process groups
    if (a.isGroup === false && b.isGroup === false) return 0;
    if (a.isGroup === false) return 1;
    if (b.isGroup === false) return -1;

    // sort by group name first
    const groupComare = stringCompare(a.groupName, b.groupName);
    if (groupComare !== 0) return groupComare;

    // sort by name when in the same group
    return stringCompare(a.name, b.name);
  },
  /**
   * testsOnly
   */
  testsOnly: function (a, b) {
    // only process tests
    if (a.isGroup && b.isGroup) return 0;
    if (a.isGroup) return -1;
    if (b.isGroup) return 1;

    // sort by test name
    return stringCompare(a.name, b.name);
  },
  /**
   * all
   */
  all: function (a, b) {
    const compare = SortByMethod.groupsOnly(a, b)
    if (compare === 0) return SortByMethod.testsOnly(a, b);
    return compare;
  }
}

/**
 * Performs a case-insensitive string comparison for sorting.
 * @param a - The first string to compare.
 * @param b - The second string to compare.
 * @returns -1 if a < b, 1 if a > b, and 0 if they are equal.
 */
function stringCompare(a: string, b: string): number {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al < bl) return -1;
  if (al > bl) return 1;
  return 0;
}
